import { z } from 'zod';
import { refersTo } from '../common/refers-to';
import { ACTIVITY_RECORD_CONTENT_MAX_LENGTH } from '../activity/activity-records.schemas';
import { AppointmentStatusSchema } from '../communications/calendar.schemas';
import { ProspectSchema, ProspectViewingTypeSchema } from './prospects.schemas';

// Array order IS the dialog's tile order.
export const PROSPECT_VIEWING_ATTENDANCES = ['ATTENDED', 'NO_SHOW'] as const;
export const PROSPECT_VIEWING_IMPRESSIONS = ['INTERESTED', 'NOT_INTERESTED', 'UNDECIDED'] as const;

export const ProspectViewingAttendanceSchema = z.enum(PROSPECT_VIEWING_ATTENDANCES);
export const ProspectViewingImpressionSchema = z.enum(PROSPECT_VIEWING_IMPRESSIONS);
export type ProspectViewingAttendance = z.infer<typeof ProspectViewingAttendanceSchema>;
export type ProspectViewingImpression = z.infer<typeof ProspectViewingImpressionSchema>;

/** The impression exists only with ATTENDED (BL-049): one owner for the rule, read by schema, CHECK and form. */
export const attendanceHasImpression = (attendance: ProspectViewingAttendance): boolean => attendance === 'ATTENDED';

export const ProspectViewingAssessmentSchema = z.object({
  attendance: ProspectViewingAttendanceSchema,
  // null exactly when attendance is NO_SHOW.
  impression: ProspectViewingImpressionSchema.nullable(),
  comment: z.string().nullable(),
  // The member who recorded it or last changed it, and when.
  recordedByMemberId: refersTo('member', z.uuid()),
  recordedAt: z.iso.datetime(),
  // True once it has been corrected.
  edited: z.boolean(),
});

// Where a Besichtigung is kept: a calendar appointment (IMM-1064.4, IMM-1067) or a composer Termin (IMM-1372).
export const PROSPECT_VIEWING_SOURCES = ['CALENDAR', 'ACTIVITY'] as const;
export const ProspectViewingSourceSchema = z.enum(PROSPECT_VIEWING_SOURCES);
export type ProspectViewingSource = z.infer<typeof ProspectViewingSourceSchema>;

export const ProspectViewingSchema = z.object({
  // The calendar appointment's id, or the activity's id when source is ACTIVITY: the assessment route's key either way.
  appointmentId: z.uuid(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  isAllDay: z.boolean(),
  status: AppointmentStatusSchema,
  source: ProspectViewingSourceSchema,
  viewingType: ProspectViewingTypeSchema.nullable(),
  assessment: ProspectViewingAssessmentSchema.nullable(),
});

export type ProspectViewingAssessment = z.infer<typeof ProspectViewingAssessmentSchema>;
export type ProspectViewing = z.infer<typeof ProspectViewingSchema>;

export const ListProspectViewingsPathParamsSchema = z.object({ workspaceId: z.uuid(), id: z.uuid() });
export const ListProspectViewingsResponseSchema = z.object({
  // The Interessent's Standort: the assessment is written on an org-addressed route.
  organizationId: refersTo('organization', z.uuid()),
  data: z.array(ProspectViewingSchema),
});

export const AssessProspectViewingPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
  appointmentId: z.uuid(),
});
// attendance and impression are not required by the schema: the assessment refuses a missing one with the PRD's
// copy, so the app and a connected client get the same refusal, never a bare 400.
export const AssessProspectViewingBodySchema = z.object({
  attendance: ProspectViewingAttendanceSchema.nullable().optional(),
  impression: ProspectViewingImpressionSchema.nullable().optional(),
  comment: z.string().trim().max(ACTIVITY_RECORD_CONTENT_MAX_LENGTH).nullable().optional(),
});
export const AssessProspectViewingResponseSchema = z.object({
  prospect: ProspectSchema,
  viewing: ProspectViewingSchema, // assessment is never null here
});

export type ListProspectViewingsResponse = z.infer<typeof ListProspectViewingsResponseSchema>;
export type AssessProspectViewingBody = z.infer<typeof AssessProspectViewingBodySchema>;
export type AssessProspectViewingResponse = z.infer<typeof AssessProspectViewingResponseSchema>;
